import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { FormField } from '../../FormField'
import { documents } from '../../../constants/documents'
import SelectorList from '../../SelectorList'
import { composeDOBYear2To4Digits, getMobileFormattedDate, validateExpirationDate, validateLength } from '../../DateInput'
import { AppState } from '../../../reducers'
import {
  setDriverDocumentTypeAction,
  setDriverExpirationDateAction,
  setDriverDocumentIssuedCountryAction,
  setDriverDocumentNumberAction,
} from '../../../actions'
import './driversDocumentMobile.less'
import { getUserDataStore, USER_INPUT_DATA } from '../../../actions/localStorage'
import { FormFieldInput } from '../../FormFieldInput'
import { INPUT_TYPE } from '../../../constants/inputTypes'
import { FormFieldCountryInput } from '../../FormFieldCountryInput'

const mapDispatchToProps = {
  setDriverDocumentType: setDriverDocumentTypeAction,
  setDriverExpirationDate: setDriverExpirationDateAction,
  setDriverDocumentIssuingCountry: setDriverDocumentIssuedCountryAction,
  setDriverDocumentNumber: setDriverDocumentNumberAction,
}
type DispatchProps = typeof mapDispatchToProps

const mapStateToProps = (state: AppState) => ({
  driverDocumentType: state.reservationChange.drivers[0].docType,
  driverExpirationDate: state.reservationChange.drivers[0].docExpirationDate,
  driverDocumentIssuingCountryCode: state.reservationChange.drivers[0].docIssuedCountry,
  driverDocumentNumber: state.reservationChange.drivers[0].docNumber,
  userTypedDriverBirthDate: state.reservation.birthDate,
})

export interface CustomDriversDocumentMobileProps {
  setDocumentInputError: (isInputError: boolean) => void
}

type StateProps = ReturnType<typeof mapStateToProps>
type Props = DispatchProps & StateProps & CustomDriversDocumentMobileProps

let driverDocumentIssuingCountryInput: HTMLInputElement | null = null

const DriversDocumentMobile: React.FC<Props> = ({
  setDriverDocumentType,
  setDriverExpirationDate,
  setDriverDocumentIssuingCountry,
  setDriverDocumentNumber,
  driverDocumentType,
  driverExpirationDate,
  driverDocumentIssuingCountryCode,
  driverDocumentNumber,
  setDocumentInputError,
  userTypedDriverBirthDate,
}: Props) => {
  const { t } = useTranslation()
  const [activeInput, setActiveInput] = useState<any>()
  const [driverDocumentIssuingCountryName, setDriverDocumentIssuingCountryName] = useState('')
  const [expirationDate, setExpirationDate] = useState({
    date: driverExpirationDate,
    dateValidation: validateExpirationDate(driverExpirationDate),
    cursorPosition: 0,
  })
  const [isCountrySelect, setIsCountrySelect] = useState(false)
  const [isNoCountry, setIsNoCountry] = useState(false)

  const driverExpirationDateInput = useRef<any>(null)
  const driverDocumentNumberInput = useRef(null)

  const [, updateState] = React.useState<any>()
  const forceUpdate = useCallback(() => updateState({}), [])

  const onChange = (value: string) => {
    switch (activeInput) {
      case driverExpirationDateInput.current:
        const dateMaxLength = 10
        if (value.length <= (dateMaxLength + 1) && driverExpirationDateInput && driverExpirationDateInput.current) {
          const data = getMobileFormattedDate(driverExpirationDateInput.current, value, dateMaxLength)
          if (!!data) {
            setExpirationDate({ date: data.value, dateValidation: validateExpirationDate(data.value), cursorPosition: data.cursorPosition })
            validateExpirationDate(data.value) && setDriverExpirationDate(data.value)
          } else {
            forceUpdate()
          }
        } else {
          forceUpdate()
        }
        break
      case driverDocumentIssuingCountryInput:
        setDriverDocumentIssuingCountryName(value)
        setIsCountrySelect(false)
        break

      case driverDocumentNumberInput.current:
        setDriverDocumentNumber(value.toUpperCase())
        break
    }
  }
  const isInputError = (
    !expirationDate.dateValidation ||
    !driverDocumentType ||
    !isCountrySelect ||
    !driverDocumentNumber ||
    !driverDocumentNumber.trim()
  )

  const isDateFieldError = !validateExpirationDate(expirationDate.date) && validateLength(expirationDate.date, 10)

  const onSelect = (option: string) => {
    setDriverDocumentIssuingCountry(option)
    setIsCountrySelect(true)
  }

  const clearInputField = (currentInput: any) => {
    setActiveInput(currentInput)
    switch (currentInput) {
      case driverExpirationDateInput.current:
        setExpirationDate({ date: '', dateValidation: false, cursorPosition: 0 })
        break
      case driverDocumentNumberInput.current:
        setDriverDocumentNumber('')
        break
      case driverDocumentIssuingCountryInput:
        setDriverDocumentIssuingCountryName('')
        setIsCountrySelect(false)
        break
    }
  }

  useEffect(() => {
    const storedDateOfBirth = getUserDataStore(USER_INPUT_DATA.DRIVER_DATE_OF_BIRTH)
    const userTypedDOB4Digits = composeDOBYear2To4Digits(userTypedDriverBirthDate)
    if (userTypedDOB4Digits === storedDateOfBirth) {
      if (!driverDocumentNumber) {
        setDriverDocumentNumber(getUserDataStore(USER_INPUT_DATA.DRIVER_DOCUMENT_NUMBER) || '')
      }
    }
  }, [])

  useEffect(() => {
    if (driverExpirationDateInput && driverExpirationDateInput.current && activeInput === driverExpirationDateInput.current) {
      driverExpirationDateInput.current.selectionStart = expirationDate.cursorPosition
      driverExpirationDateInput.current.selectionEnd = expirationDate.cursorPosition
    }
  }, [expirationDate])

  useEffect(() => {
    setDocumentInputError(isInputError)
  }, [setDocumentInputError, isInputError])

  return (
    <div className="drivers-document-mobile">
      <div className="drivers-document-title" dangerouslySetInnerHTML={{ __html: t('drivesDocumentPassenger1Document') }}></div>
      <div className="drivers-document-content">
        <div className="drivers-document-row">
          <FormField
            id="drivers-document-type"
            classFormField="form-field-mobile drivers-form-field"
            label={t('documentType')}
          >
            <SelectorList options={documents} value={driverDocumentType} onChange={setDriverDocumentType} />
          </FormField>
          <FormFieldInput
            id="drivers-document-expiration-date"
            type={INPUT_TYPE.DATE}
            classFormField={`drivers-form-field`}
            classInput={'expiration-date'}
            label={t('expirationDate')}
            isClearNeed={!!expirationDate.date}
            clearInputField={clearInputField}
            errors={{ inputError: isDateFieldError }}
            inputRef={driverExpirationDateInput}
            value={expirationDate.date}
            onChange={onChange}
            onFocus={() => {
              setActiveInput(driverExpirationDateInput.current)
            }}
            isActive={activeInput === driverExpirationDateInput.current}
          />
          <FormFieldCountryInput
            id="drivers-document-issuing-country"
            classFormField="country"
            label={t('drivesDocumentIssuingCountry')}
            sublabel={isNoCountry ? t('checkTheSpelling') : ''}
            onChange={onChange}
            onSelect={onSelect}
            setCountry={setDriverDocumentIssuingCountryName}
            countryCode={driverDocumentIssuingCountryCode}
            setIsCountrySelect={setIsCountrySelect}
            isCountrySelect={isCountrySelect}
            value={driverDocumentIssuingCountryName}
            inputRef={driverDocumentIssuingCountryInput}
            httpRef={(el: HTMLInputElement) => driverDocumentIssuingCountryInput = el}
            onFocus={() => {
              setActiveInput(driverDocumentIssuingCountryInput)
            }}
            isActive={activeInput === driverDocumentIssuingCountryInput}
            autoFocus={false}
            setIsError={setIsNoCountry}
            clearInputField={clearInputField}
            errors={{inputError: isNoCountry}}
          />
        </div>

        <div className="drivers-document-row">
        <FormFieldInput
            id="drivers-document-number"
            type={INPUT_TYPE.TEXT}
            classFormField={`drivers-form-field`}
            label={t('drivesDocumentNumber')}
            isClearNeed={!!driverDocumentNumber}
            clearInputField={clearInputField}
            errors={{ formFieldError: true }}
            inputRef={driverDocumentNumberInput}
            value={driverDocumentNumber}
            onChange={onChange}
            onFocus={() => {
              setActiveInput(driverDocumentNumberInput.current)
            }}
            isActive={activeInput === driverDocumentNumberInput.current}
          />
        </div>
      </div>
    </div>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(DriversDocumentMobile)
